import { GridApi } from "ag-grid-community";
import { ChangeEvent, MutableRefObject, useEffect, useState } from "react";
import { IPageNav } from "interface";
import { GrFormNext } from "react-icons/gr";
import { GrFormPrevious } from "react-icons/gr";
import { LuChevronLast } from "react-icons/lu";
import { LuChevronFirst } from "react-icons/lu";
import { initialPageNav, pageInitInf } from "components/utils/comm";
import usePageNoStore from "store/pageNo";
import useMdiStore from "store/menus";
import "./grid.scss";

type IPagination = {
  pageNavInf: IPageNav | null;
  gridApi: MutableRefObject<GridApi<any> | null>;
};

// Custom Pagination Controls
export default function Pagination({ pageNavInf, gridApi }: IPagination) {
  const setPagesNo = usePageNoStore((state) => state.setPagesNo);
  const pagesNo = usePageNoStore((state) => state.pageNo);
  const activePageId = useMdiStore((state) => state.activePageId);

  const [pageNo, setPageNo] = useState(1);
  const [pageRows, setPageRows] = useState(pageInitInf.pageRows);
  const [pageNav, setPageNav] = useState<IPageNav>(initialPageNav);

  const totalPage = Math.ceil(pageNav.totalRows / pageRows);

  const onFirstPage = () => {
    if (gridApi.current) {
      gridApi.current.paginationGoToLastPage();
      setPageNo(pageNav.backPageUnit);
      setPagesNo({
        target: activePageId,
        no: pageNav.backPageUnit,
        pageRows: pageRows,
        activePageId: activePageId,
      });
    }
  };
  const onPreviousPage = () => {
    setPageNo((pageNo) => pageNo - 1);
    if (gridApi.current && pageNo > 1) {
      gridApi.current.paginationGoToPreviousPage();
      setPagesNo({
        target: activePageId,
        no: pageNav.backPage,
        pageRows: pageRows,
        activePageId: activePageId,
      });
    }
  };

  const onNextPage = () => {
    setPageNo((pageNo) => pageNo + 1);
    if (gridApi.current) {
      gridApi.current.paginationGoToNextPage();
      setPagesNo({
        target: activePageId,
        no: pageNav.nextPage,
        pageRows: pageRows,
        activePageId: activePageId,
      });
    }
  };

  const onGoToPage = (page: number) => {
    setPageNo(page);
    if (gridApi.current) {
      gridApi.current.paginationGoToPage(pageNo);
      setPagesNo({
        target: activePageId,
        no: page,
        pageRows: pageRows,
        activePageId: activePageId,
      });
    }
  };

  const onLastPage = () => {
    if (gridApi.current) {
      gridApi.current.paginationGoToLastPage();
      setPagesNo({
        target: activePageId,
        no: pageNav.nextPageUnit,
        pageRows: pageRows,
        activePageId: activePageId,
      });

      setPageNo(pageNav.nextPageUnit);
    }
  };

  const onPageSizeChanged = (e: ChangeEvent<HTMLSelectElement>) => {
    const newPageRows = Number(e.target.value);
    setPageRows(newPageRows);
    // setPagesNo({
    //   target: activePageId,
    //   no: pageNo,
    //   pageRows: newPageRows,
    //   activePageId,
    // });
    setPageNo(1);
    if (gridApi.current) {
      gridApi.current.updateGridOptions({
        paginationPageSize: newPageRows,
      });
      setPagesNo({
        target: activePageId,
        no: 1,
        pageRows: newPageRows,
        activePageId: activePageId,
      });

      gridApi.current.paginationGoToFirstPage();
    }
  };

  useEffect(() => {
    if (pageNavInf) {
      setPageNav(pageNavInf);
    }
  }, [pageNavInf]);

  useEffect(() => {
    if (!pagesNo) {
      setPageNo(1);
      setPageRows(pageInitInf.pageRows);
    }
  }, [pagesNo]);

  const pageForArray = (endUnitPage: number, beginUnitPage: number) => {
    const length = endUnitPage - beginUnitPage + 1;
    return Array.from({ length: length }, (v, i) => i + beginUnitPage);
  };

  const pageUnits = pageForArray(pageNav.endUnitPage, pageNav.beginUnitPage);

  return (
    <div className="pagination-controls">
      <div>
        <span> Page Size:&nbsp;&nbsp;</span>
        <select value={pageRows} onChange={(e) => onPageSizeChanged(e)}>
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={40}>40</option>
          <option value={100}>100</option>
        </select>
      </div>

      <div className={"flex gap-1 items-center "}>
        {pageNo > 10 && (
          <button onClick={onFirstPage} disabled={pageNo === 1}>
            <LuChevronFirst size={18} color={pageNo === 1 ? "#ced4da" : ""} />
          </button>
        )}
        {pageNo > 1 && (
          <button onClick={onPreviousPage} disabled={pageNo === 1}>
            <GrFormPrevious size={20} color={pageNo === 1 ? "#ced4da" : ""} />
          </button>
        )}
        <ul className="flex ">
          {pageUnits.map((page) => {
            return (
              <li key={page}>
                <button
                  type="button"
                  className={pageNo === page ? "current" : ""}
                  onClick={() => onGoToPage(page)}
                >
                  {page}
                </button>
              </li>
            );
          })}
        </ul>

        {pageNav.nextPageUnit && pageNav.totalRows > pageNav.pageUnit && (
          <button onClick={onNextPage} disabled={pageNo === totalPage}>
            <GrFormNext
              size={20}
              color={pageNo === totalPage ? "#ced4da" : ""}
            />
          </button>
        )}
        {totalPage > pageNav.nextPageUnit && (
          <button
            onClick={onLastPage}
            // disabled={totalPage > pageNav.nextPageUnit}
          >
            <LuChevronLast
              size={18}
              color={totalPage > pageNav.nextPageUnit ? "" : "#ced4da"}
            />
          </button>
        )}
      </div>
      <div className="lg:flex gap-6">
        <span className="px-2">
          Row&nbsp;:&nbsp;{pageNo}&nbsp;&nbsp;to&nbsp;&nbsp;{pageRows}
          &nbsp;&nbsp;of&nbsp;&nbsp;{pageNav.totalRows}
        </span>
        <span className="px-2">
          Page&nbsp;:&nbsp;{pageNo}&nbsp;&nbsp;to&nbsp;&nbsp;
          {totalPage}
        </span>
      </div>
    </div>
  );
}
